import { motion } from "framer-motion";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModal, useViewport } from "src/contexts";
import {
  CommunityOrLotStepper,
  FooterHeight,
  ImageDisplay,
  ImageDisplayImageProps,
  StepWrapper,
  ToggleComponent,
  WarningModal,
} from "src/pages/configurator/components";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { startSaveVital } from "src/utils/analytics";
import {
  Checkbox,
  fetchAndCacheSVG,
  HBLoadingSpinnerOnly,
  OptionPreviewImage,
  Radio,
  SelectableOptionCard,
} from "~components";
import { Css } from "~generated/css";
import {
  fail,
  formatAmount,
  getPlanBathroomRangeString,
  getPlanBedroomRangeString,
  getPlanSqftRangeString,
  queryResult,
  useTestIds,
} from "~utils";
import { createConfiguratorUrl, createLotsUrl } from "../../../RouteUrls";
import { useComputed } from "../../../utils/hooks/useComputed";
import { UserWarning } from "../components";
import { ConfiguratorContext_CheckoutConfigFragment } from "../Configurator.context.generated";
import { getFloorPlanImages } from "../Configurator.utils";
import { FlowStep } from "./FlowSteps";
import { PlanStep_ProductOfferingFragment, usePlanStepQuery } from "./Plan.step.generated";

/** Show the Plans/ProductOfferings for the selected Lot/Structure. */
export function PlanStep() {
  const { communityId, formState } = useConfiguratorContext();
  const query = usePlanStepQuery({
    variables: { communityId: communityId ?? fail("Missing communityId") },
  });
  const { isMobile } = useViewport();

  return queryResult(query, {
    data: (data) => {
      const plans = data.productOfferings.entities;
      // Cache the floor plan svgs early
      plans.forEach((plan) => {
        plan.floorPlanAssets?.forEach((floorPlanAsset) => {
          void fetchAndCacheSVG(floorPlanAsset.asset.downloadUrl);
        });
      });
      // Our formstate is loading it's own query (seperate from an autosave), so we show the loading view
      // again until it's returned so we dont have our content and it's state jumping around.
      return formState.loading ? <PlanStepLoadView isMobile={isMobile} /> : <PlanStepView plans={plans} />;
    },
    loading: () => {
      return <PlanStepLoadView isMobile={isMobile} />;
    },
  });
}

export function PlanStepView(props: { plans: PlanStep_ProductOfferingFragment[] }) {
  const { formState, checkoutConfig, communityId, autoSaving } = useConfiguratorContext();
  const { plans } = props;
  const tid = useTestIds({}, "market-plans-step");
  const navigate = useNavigate();
  const { showModal, closeModal } = useModal();
  const { isMobile } = useViewport();
  const [planInfoCardHeight, setPlanInfoCardHeight] = useState(0);
  const planInfoCardRef = useRef<HTMLDivElement>(null);

  // Toggle between "Exterior | Floor Plans"
  const [viewFloorplan, setViewFloorplan] = useState(false);

  // Sort plans based on the initial productOfferingId on first render
  // to pull currently selected plan to the top of the list
  const sortedPlans = useMemo(() => {
    const initialProductOfferingId = formState.productOfferingId.value;
    if (!initialProductOfferingId) return plans;
    return [...plans].sort((a, b) => +(b.id === initialProductOfferingId) - +(a.id === initialProductOfferingId));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sort once on initial render
  }, []);

  const [productOfferingId, isReversed = false, loading] = useComputed(
    () => [formState.productOfferingId.value, formState.isReversed.value, formState.loading],
    [formState],
  );

  const isDisabled = !checkoutConfig?.canChangeProductOffering.allowed || !checkoutConfig?.canEdit.allowed;
  const currentPlan = plans.find((p) => p.id === productOfferingId);

  const images = getCurrentPlanImages(viewFloorplan, currentPlan, !!isReversed);

  /**
   * For suburban developments, we can specify at the `Lot` level which product offerings/plans will fit on a given lot.
   * However if no buildable plans are specified, we assume all plans are buildable.
   */
  const isPlanBuildable = useCallback(
    (planId: string) => {
      const maybeBuildablePlans = checkoutConfig?.structure?.parent?.buildablePlans;

      if (!maybeBuildablePlans || maybeBuildablePlans.isEmpty) return true;

      // The buildable plans list doesn't take into account if the plan is "show in checkout"
      // so we must compare the plan ids here vs just doing a more naive length check across both lists.
      return maybeBuildablePlans.some((p) => p.id === planId);
    },
    [checkoutConfig],
  );

  const maybeStepWarning = useMemo(() => {
    if (loading) return null;

    // Post-cutoff/disabled takes priority, we don't care if some plans might not be buildable in this case.
    if (isDisabled) {
      return (
        <UserWarning
          title="The floor plan can no longer be changed."
          disabledReasons={[checkoutConfig?.canEdit, checkoutConfig?.canChangeProductOffering]}
        />
      );
    }

    // Check that all plans are buildable
    if (plans.some((p) => !isPlanBuildable(p.id))) {
      return <UserWarning title="This lot does not support all plans." />;
    }
    return null;
  }, [loading, isDisabled, checkoutConfig, plans, isPlanBuildable]);

  return (
    <>
      {/* Mobile view is different enough that we handle it separately */}
      {isMobile ? (
        <>
          <div
            // Add padding to the bottom of this container to account for plan info card height and footer height
            css={Css.df.fdc.oa.bgWhite.pt1.px2.gap1.pbPx(planInfoCardHeight + FooterHeight + 16).$}
          >
            <PlanStepDescription />
            {maybeStepWarning}
            <div css={Css.df.fdc.gap2.$}>
              {sortedPlans.map((plan) => (
                <div key={plan.id} css={Css.df.fdc.$}>
                  <div css={Css.df.aic.jcsb.py2.$}>
                    <div css={Css.t18.$}>{plan.name}</div>
                    <div css={Css.df.gap1.aic.$}>
                      <div css={Css.t14.$}>{getFormattedPlanDisplayPrice(plan, checkoutConfig)}</div>
                      <Radio
                        selected={productOfferingId === plan.id}
                        disabled={!isPlanBuildable(plan.id) || isDisabled}
                        onClick={() => {
                          startSaveVital(true);
                          formState.productOfferingId.set(plan.id);
                        }}
                      />
                    </div>
                  </div>
                  <div css={Css.ha.mhPx(200).$}>
                    <OptionPreviewImage
                      optionName={plan.name}
                      imageUrl={plan.rendering?.largePreview}
                      size={{ width: "100%", height: "100%" }}
                      hideBorder={true}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          {currentPlan && (
            <motion.div
              ref={planInfoCardRef}
              css={Css.df.fdc.bgWhite.p2.ba.bcLightGray.fixed.w100.bottomPx(FooterHeight).$}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              onAnimationComplete={() => {
                if (planInfoCardRef.current) {
                  setPlanInfoCardHeight(planInfoCardRef.current.offsetHeight);
                }
              }}
            >
              <div css={Css.df.aic.jcsb.pb1.$}>
                <div css={Css.t18.$}>{currentPlan.name}</div>
                <Radio selected={true} />
              </div>
              <PlanStepCardContent plan={currentPlan} checkoutConfig={checkoutConfig} />
              <PlanReversedCheckbox
                isDisabled={isDisabled}
                autoSaving={autoSaving}
                isReversed={!!isReversed}
                onToggle={() => formState.isReversed.set(!isReversed)}
              />
            </motion.div>
          )}
        </>
      ) : (
        <StepWrapper
          loading={loading}
          loadingView="selectable"
          title="Floor Plan"
          description={<PlanStepDescription />}
          warningNode={maybeStepWarning}
          left={
            <>
              {/* Toggle Container */}
              <div css={Css.z1.absolute.topPx(24).left("50%").add("transform", "translate(-50%)").$}>
                <ToggleComponent
                  offLabel="Exterior"
                  onLabel="Floor Plans"
                  value={viewFloorplan}
                  onClick={() => setViewFloorplan(!viewFloorplan)}
                  {...tid.toggle}
                />
              </div>
              <div css={Css.df.fdc.h100.w100.if(viewFloorplan).bgOffWhite.$}>
                <ImageDisplay images={images} isReversed={isReversed ?? false} {...tid.imageDisplay} />
              </div>
            </>
          }
          right={sortedPlans.map((plan) => (
            <SelectableOptionCard
              key={plan.id}
              title={plan.name}
              description={<PlanStepCardContent plan={plan} checkoutConfig={checkoutConfig} />}
              onClick={() => {
                startSaveVital(true);
                formState.productOfferingId.set(plan.id);
                // Don't bother calling `setOptionIds` b/c changing the POF will have the BE reset the ccfg.readyPlanOptions
                // and also fill in any missing/stale RPOGs, so just let it work.
              }}
              selected={productOfferingId === plan.id}
              disabled={!isPlanBuildable(plan.id) || isDisabled || autoSaving}
            />
          ))}
          rightFooter={
            <PlanReversedCheckbox
              isDisabled={isDisabled}
              autoSaving={autoSaving}
              isReversed={!!isReversed}
              onToggle={() => formState.isReversed.set(!isReversed)}
            />
          }
        />
      )}
      <CommunityOrLotStepper
        onBack={() => {
          showModal(
            <WarningModal
              title="Are you sure?"
              confirmationMessage="Selecting a new lot will require an entirely new configuration."
              onContinue={() => {
                navigate(createLotsUrl(communityId!));
              }}
              onClose={closeModal}
            />,
          );
        }}
        onNext={() => {
          navigate(createConfiguratorUrl(communityId!, checkoutConfig!.id, FlowStep.Exterior));
        }}
      />
    </>
  );
}

/**
 * If the plan is selected, use the checkout config's sales price, otherwise use the plan's base config sales price.
 * Returns formatted amount string (e.g. "$350,000") or empty string if no price available.
 */
function getFormattedPlanDisplayPrice(
  plan: PlanStep_ProductOfferingFragment,
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined,
): string {
  const isSelectedPlan = checkoutConfig?.productOffering?.id === plan.id;
  const priceToDisplay = isSelectedPlan ? checkoutConfig?.salesPriceInCents : plan.baseConfig?.salesPriceInCents;

  return formatAmount(priceToDisplay ?? 0);
}

/** Show the Beds / Baths / Sqft for each plan. */
function PlanStepCardContent(props: {
  plan: PlanStep_ProductOfferingFragment;
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined;
}) {
  const { plan, checkoutConfig } = props;
  const tid = useTestIds({}, "plan-step-card-content");

  const hasBaseSalesPrice = !!plan.baseConfig?.salesPriceInCents;

  if (!hasBaseSalesPrice) {
    console.error(
      `${plan.name}'s base config '${plan.baseConfig?.name}' is being shown in checkout but has no defined sales price.`,
    );
  }

  const formattedPrice = getFormattedPlanDisplayPrice(plan, checkoutConfig);

  return (
    <div css={Css.df.fdc.gap1.$}>
      {/* NOTE: We will ALWAYS have a baseConfig as it is a validation rule to even pull POFs into the configurator in the first place. */}
      {hasBaseSalesPrice && (
        <div css={Css.t14.gray700.$} {...tid.planSalesPrice}>{`Starting at ${formattedPrice}`}</div>
      )}
      <div css={Css.df.flexWrap("wrap").colGap(3).rowGap(1).$}>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanBedroomRangeString(plan)}</p>
        </div>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanBathroomRangeString(plan)}</p>
        </div>
        <div css={Css.df.aic.$}>
          <p css={Css.t14.gray700.$}>{getPlanSqftRangeString(plan)}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Get the images to display for the current plan based on the view mode.
 * Returns floor plan images when viewing floor plans, otherwise returns rendering images.
 */
function getCurrentPlanImages(
  viewFloorplan: boolean,
  currentPlan: PlanStep_ProductOfferingFragment | undefined,
  isReversed: boolean,
): ImageDisplayImageProps[] {
  if (!currentPlan) return [];

  const floorPlanImages = getFloorPlanImages(currentPlan, isReversed);
  if (viewFloorplan && floorPlanImages.nonEmpty) {
    return floorPlanImages;
  }

  if (currentPlan.rendering) {
    return [
      {
        kind: "image",
        isRendering: true,
        id: currentPlan.rendering.id,
        imageUrl: currentPlan.rendering.largePreview!,
        isReversed,
        name: currentPlan.planPackage?.name,
        description: currentPlan.planPackage?.description,
      },
    ];
  }

  return [];
}

function PlanReversedCheckbox(props: {
  isDisabled: boolean;
  autoSaving: boolean;
  isReversed: boolean;
  onToggle: () => void;
}) {
  const { isDisabled, autoSaving, isReversed, onToggle } = props;
  const tid = useTestIds({}, "plan-reversed-checkbox");
  return (
    <div css={Css.df.aic.gap1.ifMobile.mt3.$}>
      <Checkbox disabled={isDisabled || autoSaving} selected={isReversed} onClick={onToggle} {...tid.isReversed} />
      <p css={Css.ml1.$}>Reverse Plan</p>
    </div>
  );
}

function PlanStepDescription() {
  return (
    <div css={Css.df.fdc.gap1.t14.lh("1.25em").gray.$}>
      Choose from one of our thoughtfully designed floor plans to get started. Next, you can pick your exterior style
      and add on more features.
    </div>
  );
}

function PlanStepLoadView({ isMobile }: { isMobile: boolean }) {
  return (
    <>
      {isMobile ? (
        <div css={Css.df.fdc.oa.bgWhite.py1.px2.gap2.fg1.$}>
          <PlanStepDescription />
          {/* Center the spinner a bit more vertically*/}
          <div css={Css.df.jcc.aic.fg1.mh("55vh").$}>
            <HBLoadingSpinnerOnly />
          </div>
        </div>
      ) : (
        <StepWrapper
          loading
          loadingView="selectable"
          title="Floor Plan"
          description={
            <div css={Css.df.fdc.gap1.$}>
              Choose from one of our thoughtfully designed floor plans to get started. Next, you can pick your exterior
              style and add on more features.
            </div>
          }
          left={<ImageDisplay images={[]} />}
          right={<></>}
        />
      )}
      <CommunityOrLotStepper onBack={() => {}} onNext={() => {}} />
    </>
  );
}
